import { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Wordmark } from "./brand";
import { InteractiveBackdrop } from "./InteractiveBackdrop";
import { SiteFooter } from "./SiteFooter";

/**
 * AuthLayout - the shared product chrome for EVERY auth screen (Sign in, Sign up,
 * Verify email, Forgot/reset password).
 *
 * Brings the auth flow onto the SAME chrome as the public landing + the authed
 * app shell so the whole product reads as one piece:
 *   - the shared InteractiveBackdrop (radar sweep + topographic contours, NOT the
 *     old per-page grid + orbs),
 *   - the shared brand Wordmark in a sticky header (clicking it returns to the
 *     public landing),
 *   - the shared SiteFooter (author credit + "Built on AWS"), variant="landing"
 *     so its in-page anchors point at the marketing page.
 *
 * The auth FORM (card, fields, handlers) is passed in as children and rendered
 * untouched, centered in the page between header and footer.
 */
export function AuthLayout({ children }: { children: ReactNode }) {
  const navigate = useNavigate();

  return (
    <div className="relative min-h-screen flex flex-col overflow-hidden">
      <InteractiveBackdrop />

      {/* Shared brand header - matches the landing + app shell chrome. */}
      <header className="sticky top-0 z-30 border-b border-[#1a2640]/80 bg-[#020617]/80 backdrop-blur-xl">
        <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-brand-600/25 to-transparent" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate("/")}
            className="cursor-pointer shrink-0"
            aria-label="NovaSynth home"
          >
            <Wordmark size="md" showTagline />
          </button>
          <span className="hidden md:inline-flex items-center gap-2 rounded-full border border-emerald-700/30 bg-emerald-500/5 px-2.5 py-1 text-[11px] font-medium text-emerald-300">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
            Secure sign-in
          </span>
        </div>
      </header>

      {/* The auth card, centered between header + footer. */}
      <main className="relative z-10 flex-1 flex items-center justify-center px-4 py-12">
        {children}
      </main>

      <SiteFooter variant="landing" />
    </div>
  );
}
